import React, { useMemo } from 'react';
import { Image } from 'antd';
import type { BaseComponentProps, DataSetConfig } from '../../types/common';
import { useAndStore } from '../../base/store/useAndStore';
import { getDataSetStoreKey } from '../../base/storeKeyUtils';
import { getCoreConfig } from '../../config';
import { prepareAntdDomProps } from '../../utils/normalizeReactStyle';

const { PreviewGroup } = Image;

/** 将相对路径 src 转为完整访问地址（含 auth_token），data:/blob:/http 等保持原样 */
function toDisplaySrc(src: string | undefined): string | undefined {
  if (!src || typeof src !== 'string') return src;
  if (/^(data:|blob:|https?:\/\/)/i.test(src)) return src;
  const viewRes = getCoreConfig().viewRes;
  return viewRes ? viewRes(src) : src;
}

// Use a simpler type since GroupConsumerProps is not exported
export interface AndImageGroupProps extends BaseComponentProps, DataSetConfig {
  children?: React.ReactNode;
  preview?: boolean;
  items?: string[];
}

export const AndImageGroup: React.FC<AndImageGroupProps> = ({
  id, sessionId, develop, children, dataSet, dataSetField, items: propItems, ...restProps
}) => {
  const getDataSet = useAndStore((state) => state.getDataSet);
  const getProps = useAndStore((state) => state.getProps);
  const dataSetKey = useMemo(
    () => getDataSetStoreKey(sessionId, dataSet),
    [sessionId, dataSet]
  );

  const storeProps = useMemo(() => {
    if (sessionId && id) return getProps(sessionId, id);
    return null;
  }, [sessionId, id, getProps]);

  const storeValue = useMemo(() => {
    if (dataSetKey && dataSetField) return getDataSet(dataSetKey, dataSetField);
    return undefined;
  }, [dataSetKey, dataSetField, getDataSet]);

  // 设计器模式(develop)：优先 store，无则回退到静态 items；运行时且配置了 dataSet：仅用 store，不回退到静态 items
  const rawItems = useMemo(() => {
    const storeItems =
      storeValue !== undefined && storeValue !== null
        ? (Array.isArray(storeValue) ? storeValue : typeof storeValue === 'string' ? (() => { try { return JSON.parse(storeValue); } catch { return undefined; } })() : undefined)
        : storeProps?.items;
    if (develop) return storeItems ?? propItems ?? [];
    if (dataSetKey) return Array.isArray(storeItems) ? storeItems : [];
    return propItems ?? [];
  }, [develop, dataSetKey, storeValue, storeProps?.items, propItems]);

  // 通过 viewRes 将相对路径转为完整地址，支持后台 src 展示与预览（items 可为 string[] 或 { url?/src? }[]）
  const computedItems = useMemo(() => {
    return rawItems
      .map((item: string | { url?: string; src?: string }) => {
        const src = typeof item === 'string' ? item : (item && typeof item === 'object' ? item.url ?? item.src : undefined);
        return toDisplaySrc(src);
      })
      .filter((s: string | undefined): s is string => Boolean(s));
  }, [rawItems]);

  const mergedProps = useMemo(() => {
    const baseProps = { ...restProps };
    if (storeProps && typeof storeProps === 'object') {
      Object.keys(storeProps).forEach((key) => {
        if (key !== 'items' && storeProps[key] !== undefined && storeProps[key] !== null) {
          (baseProps as any)[key] = storeProps[key];
        }
      });
    }
    return baseProps;
  }, [restProps, storeProps]);

  const groupDomProps = useMemo(
    () => prepareAntdDomProps(mergedProps as Record<string, unknown>, ['token']),
    [mergedProps],
  );

  return (
    <PreviewGroup {...groupDomProps} items={computedItems}>
      {children}
    </PreviewGroup>
  );
};

export default AndImageGroup;

